import { admessageService } from './admessage.service';
import { blockedwordService } from './blockedword.service';
import { chatService } from './chat.service';
import { configService } from './config.service';
import { fontService } from './font.service';
import { gameService } from './game.service';
import { profileService } from './profile.service';
import { questionService } from './question.service';
import { studentService } from './student.service';
import { userService } from './user.service';
import {
    collectMediaUrls,
    extractLandingMediaUrls,
    extractQuestionMediaUrls,
    preloadMediaUrls,
} from '../helpers/mediaPreloader';

const IDLE_PREFETCH_TIMEOUT_MS = 1500;

function scheduleIdleTask(task, timeout = IDLE_PREFETCH_TIMEOUT_MS) {
    if (typeof window === 'undefined') {
        return () => {};
    }

    if (typeof window.requestIdleCallback === 'function') {
        const idleId = window.requestIdleCallback(() => task(), { timeout });
        return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(task, Math.min(timeout, 400));
    return () => window.clearTimeout(timeoutId);
}

function shouldConserveData() {
    const connection =
        typeof navigator !== 'undefined'
            ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection)
            : null;

    if (!connection) return false;
    if (connection.saveData) return true;

    const effectiveType = String(connection.effectiveType || '').toLowerCase();
    return effectiveType === 'slow-2g' || effectiveType === '2g';
}

function safelyRun(task) {
    Promise.resolve()
        .then(() => task())
        .catch(() => undefined);
}

function unwrapData(response) {
    return response?.data ?? response ?? null;
}

function preloadLandingMedia(mediaResponse) {
    const landingMedia = unwrapData(mediaResponse);
    preloadMediaUrls(extractLandingMediaUrls(landingMedia));
    return landingMedia;
}

function preloadProfileMedia(profileResponse) {
    const profile = unwrapData(profileResponse);
    preloadMediaUrls(collectMediaUrls(profile, ['AvatarMedia']));
    return profile;
}

function preloadChatMedia(chatResponse) {
    const messages = unwrapData(chatResponse);
    preloadMediaUrls(collectMediaUrls(messages, ['SenderProfileImage']));
    return messages;
}

function preloadQuestionMedia(questionResponse) {
    const question = unwrapData(questionResponse);
    preloadMediaUrls(extractQuestionMediaUrls(question), { fetchpriority: 'high' });
    return question;
}

function buildRouteTasks(pathname, user) {
    const data = user?.data;
    if (!data) return [];

    const studentProfileId = data.StudentProfileID;
    const classId = data.ClassID;
    const gameMenu = data.GameMenu;

    switch (pathname) {
        case '/':
            return [
                ...(gameMenu != null ? [() => gameService.fetchGames(studentProfileId, gameMenu)] : []),
                () => gameService.fetchBlockedStatus(studentProfileId),
                () => profileService.fetchMypoints(studentProfileId),
            ];
        case '/mypoints':
            return [
                () => profileService.fetchMypoints(studentProfileId),
            ];
        case '/scoreboard':
            return classId
                ? [() => profileService.fetchScoreBoard(classId)]
                : [];
        case '/myprofile':
            return [
                () => profileService.fetchMyprofile(studentProfileId).then(preloadProfileMedia),
                () => userService.fetchprofileavatars(studentProfileId),
                () => profileService.fetchMypoints(studentProfileId),
            ];
        case '/editprofile':
            return [
                () => profileService.fetchMyprofile(studentProfileId).then(preloadProfileMedia),
            ];
        case '/editfavoriteactivities':
            return [
                () => profileService.fetchMyprofile(studentProfileId),
                () => userService.fetchFavActivities(),
            ];
        case '/purchaseavatar':
            return [
                () => userService.fetchprofileavatars(studentProfileId),
                () => profileService.fetchMypoints(studentProfileId),
                () => profileService.fetchMyprofile(studentProfileId).then(preloadProfileMedia),
            ];
        case '/purchasegame':
            return [
                ...(gameMenu != null ? [() => gameService.fetchGames(studentProfileId, gameMenu)] : []),
                () => gameService.fetchBlockedStatus(studentProfileId),
                () => profileService.fetchMypoints(studentProfileId),
            ];
        case '/chat':
            return [
                () => userService.fetchLandingMedia().then(preloadLandingMedia),
                () => gameService.fetchBlockedStatus(studentProfileId),
                () => blockedwordService.fetchBlockedWords(),
                ...(classId ? [() => chatService.fetchChatMessages(classId).then(preloadChatMedia)] : []),
                ...(classId ? [() => studentService.fetchStudentsList(classId)] : []),
                ...(classId ? [() => profileService.fetchScoreBoard(classId)] : []),
            ];
        default:
            return [];
    }
}

export const prefetchService = {
    warmStudentSession,
    prefetchPageData,
    prefetchGameQuestionEntry,
};

function warmStudentSession(user) {
    const data = user?.data;
    if (!data?.StudentProfileID) {
        return () => {};
    }

    const lightTasks = [
        () => configService.fetchAppConfigs(),
        () => fontService.fetchFontDetails(),
        () => userService.fetchLandingMedia().then(preloadLandingMedia),
        () => questionService.fetchCoinImage(),
        () => profileService.fetchMypoints(data.StudentProfileID),
        () => gameService.fetchBlockedStatus(data.StudentProfileID),
        () => profileService.fetchMyprofile(data.StudentProfileID).then(preloadProfileMedia),
        () => userService.fetchprofileavatars(data.StudentProfileID),
    ];

    const heavyTasks = shouldConserveData()
        ? []
        : [
            () => userService.fetchFavActivities(),
            ...(data.ClassID ? [() => profileService.fetchScoreBoard(data.ClassID)] : []),
            ...(data.ClassID ? [() => chatService.fetchChatMessages(data.ClassID).then(preloadChatMedia)] : []),
            ...(data.ClassID ? [() => studentService.fetchStudentsList(data.ClassID)] : []),
            () => blockedwordService.fetchBlockedWords(),
            () => admessageService.fetchAdMessage(),
            ...(data.GameMenu != null ? [() => gameService.fetchGames(data.StudentProfileID, data.GameMenu)] : []),
        ];

    return scheduleIdleTask(() => {
        [...lightTasks, ...heavyTasks].forEach(safelyRun);
    });
}

function prefetchPageData(pathname, user) {
    const tasks = buildRouteTasks(pathname, user);
    if (tasks.length === 0) {
        return Promise.resolve();
    }

    tasks.forEach(safelyRun);
    return Promise.resolve();
}

function prefetchGameQuestionEntry({ studentProfileId, gameId }) {
    if (!studentProfileId || !gameId) {
        return Promise.resolve(null);
    }

    const tasks = [
        questionService.fetchCoinImage(),
        userService.fetchLandingMedia().then(preloadLandingMedia),
        questionService.fetchQuestion(studentProfileId, gameId, null, null, 'next').then(preloadQuestionMedia),
    ];

    return Promise.allSettled(tasks).then((results) => {
        const questionResult = results[2];
        return questionResult?.status === 'fulfilled' ? questionResult.value : null;
    });
}
